import { useEffect, useMemo, useState } from "react";
import { Building2, Plus, X } from "lucide-react";
import MonthlyProcessesView from "./MonthlyProcessesView";
import TECHNOPARKS from "../data/technoparks";
import { getTechnoparkShortName } from "../helpers/technoparkName";
import { resolveTenantId } from "../services/supportService";
import {
  createTenantMonthlyProcessBoard,
  subscribeTenantMonthlyProcessBoards,
  updateTenantMonthlyProcessBoard
} from "../services/tenantMonthlyProcessService";

const OTHER_TECHNOPARK = "__other__";

function TenantMonthlyProcessesView({ user, userData = {}, firmaListesi = [], themeMode = "light" }) {
  const tenantId = resolveTenantId(userData);
  const dark = themeMode === "dark";
  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState("");
  const [showBoardModal, setShowBoardModal] = useState(false);
  const [boardSelection, setBoardSelection] = useState("");
  const [customBoardName, setCustomBoardName] = useState("");
  const [boardShortName, setBoardShortName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!tenantId) {
      setError("Hesabınıza bağlı müşteri portalı bulunamadı.");
      return undefined;
    }

    return subscribeTenantMonthlyProcessBoards({
      tenantId,
      onData: (items) => {
        const activeBoards = items.filter((item) => item.isActive !== false);
        setBoards(activeBoards);
        setSelectedBoardId((current) =>
          activeBoards.some((item) => item.id === current) ? current : activeBoards[0]?.id || ""
        );
      },
      onError: (nextError) => setError(nextError.message || "Teknopark kanbanları yüklenemedi.")
    });
  }, [tenantId]);

  const processProjects = useMemo(() => boards.map((board) => ({
    ...board,
    name: getTechnoparkShortName(board.technoparkName, board.technoparkShortName),
    type: "kanban"
  })), [boards]);
  const selectedBoard = processProjects.find((item) => item.id === selectedBoardId) || null;

  const createBoard = async () => {
    const technoparkName = boardSelection === OTHER_TECHNOPARK
      ? customBoardName.trim()
      : boardSelection;
    if (!technoparkName) {
      setError("Teknopark adı zorunludur.");
      return;
    }

    const technoparkShortName = getTechnoparkShortName(technoparkName, boardShortName);
    if (boards.some((item) =>
      item.technoparkName.toLocaleLowerCase("tr") === technoparkName.toLocaleLowerCase("tr")
    )) {
      setError("Bu teknopark için kanban zaten bulunuyor.");
      return;
    }

    setSaving(true);
    try {
      const reference = await createTenantMonthlyProcessBoard({
        tenantId,
        technoparkName,
        technoparkShortName,
        user
      });
      setSelectedBoardId(reference.id);
      setBoardSelection("");
      setCustomBoardName("");
      setBoardShortName("");
      setShowBoardModal(false);
      setError("");
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setSaving(false);
    }
  };

  const renameBoard = async (board) => {
    const nextName = await window.appPrompt("Teknopark kanbanı adını düzenleyin.", {
      title: "Teknopark Kanbanını Düzenle",
      type: "info",
      confirmText: "Kaydet",
      inputPlaceholder: "Teknopark adı",
      defaultValue: board.technoparkName || ""
    });
    if (!nextName) return;
    const cleanName = nextName.trim();
    const nextShortName = await window.appPrompt("Kanban sekmesinde gösterilecek kısa adı yazın.", {
      title: "Teknopark Kısa Adı",
      type: "info",
      confirmText: "Kaydet",
      inputPlaceholder: "Örn. Dijitalpark",
      defaultValue: getTechnoparkShortName(cleanName, board.technoparkShortName)
    });
    if (!nextShortName) return;
    try {
      await updateTenantMonthlyProcessBoard({
        tenantId,
        boardId: board.id,
        data: {
          technoparkName: cleanName,
          technoparkShortName: nextShortName.trim()
        }
      });
    } catch (nextError) {
      setError(nextError.message);
    }
  };

  const archiveBoard = async (board) => {
    const confirmed = await window.appConfirm(
      `${board.technoparkName} kanbanı menüden kaldırılacak. Süreç kartları silinmeyecek. Devam edilsin mi?`,
      { title: "Teknopark kanbanı pasifleştirilsin mi?", type: "danger", confirmText: "Pasifleştir" }
    );
    if (!confirmed) return;
    try {
      await updateTenantMonthlyProcessBoard({ tenantId, boardId: board.id, data: { isActive: false } });
    } catch (nextError) {
      setError(nextError.message);
    }
  };

  const moveBoard = async (boardIndex, direction) => {
    const targetIndex = boardIndex + direction;
    if (targetIndex < 0 || targetIndex >= boards.length) return;
    const current = boards[boardIndex];
    const target = boards[targetIndex];
    try {
      await Promise.all([
        updateTenantMonthlyProcessBoard({ tenantId, boardId: current.id, data: { order: Number(target.order || targetIndex) } }),
        updateTenantMonthlyProcessBoard({ tenantId, boardId: target.id, data: { order: Number(current.order || boardIndex) } })
      ]);
    } catch (nextError) {
      setError(nextError.message);
    }
  };

  if (!selectedBoard) {
    return <>
      <div style={{ color: dark ? "#e5edf6" : "#124254" }}>
        <div style={{ marginBottom: 18 }}>
          <h2 style={{ margin: 0 }}>Aylık Süreçler</h2>
          <div style={{ color: dark ? "#9fb0c4" : "#64748b", fontWeight: 700, marginTop: 5 }}>
            Teknopark bazlı tam kapsamlı süreç yönetimi
          </div>
        </div>
        {error && <ErrorMessage message={error}/>} 
        <div style={{ padding: 52, textAlign: "center", borderRadius: 18, border: `1px dashed ${dark ? "#334155" : "#cbd5e1"}`, background: dark ? "#101827" : "#fff" }}>
          <Building2 size={38}/>
          <h3>İlk teknopark kanbanını oluşturun</h3>
          <p style={{ color: dark ? "#9fb0c4" : "#64748b" }}>
            Adımlar, notlar, hatırlatma taslakları, sorumlular ve süre kayıtları bu kanban altında tutulacak.
          </p>
          <button onClick={() => setShowBoardModal(true)} style={primaryButton}><Plus size={17}/> Teknopark Kanbanı</button>
        </div>
      </div>
      {showBoardModal && <BoardModal {...{ boardSelection, setBoardSelection, customBoardName, setCustomBoardName, boardShortName, setBoardShortName, createBoard, saving, dark }} onClose={() => setShowBoardModal(false)}/>} 
    </>;
  }

  return <>
    {error && <ErrorMessage message={error}/>} 
    <MonthlyProcessesView
      firmaListesi={firmaListesi}
      user={{ ...user, isAdmin: true, isEditor: true }}
      users={[user]}
      processProject={selectedBoard}
      processProjects={processProjects}
      selectProcessProject={setSelectedBoardId}
      createNewProcessProject={() => setShowBoardModal(true)}
      renameProcessProject={renameBoard}
      archiveProcessProject={archiveBoard}
      moveProcessProject={moveBoard}
      themeMode={themeMode}
      tenantMode
      tenantId={tenantId}
    />
    {showBoardModal && <BoardModal {...{ boardSelection, setBoardSelection, customBoardName, setCustomBoardName, boardShortName, setBoardShortName, createBoard, saving, dark }} onClose={() => setShowBoardModal(false)}/>} 
  </>;
}

function BoardModal({ boardSelection, setBoardSelection, customBoardName, setCustomBoardName, boardShortName, setBoardShortName, createBoard, saving, dark, onClose }) {
  const selectedTechnoparkName = boardSelection === OTHER_TECHNOPARK
    ? customBoardName
    : boardSelection;
  const suggestedShortName = getTechnoparkShortName(selectedTechnoparkName);

  const changeBoardSelection = (event) => {
    const nextSelection = event.target.value;
    setBoardSelection(nextSelection);
    setBoardShortName(nextSelection === OTHER_TECHNOPARK ? "" : getTechnoparkShortName(nextSelection));
  };

  return <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 30000, background: "rgba(15,23,42,.55)", display: "grid", placeItems: "center", padding: 18 }}>
    <div onClick={(event) => event.stopPropagation()} style={{ width: "min(540px,94vw)", display: "grid", gap: 12, padding: 21, borderRadius: 18, background: dark ? "#172234" : "#fff", color: dark ? "#e5edf6" : "#124254", boxShadow: "0 24px 70px rgba(0,0,0,.3)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <div><h3 style={{ margin: 0 }}>Teknopark Kanbanı Oluştur</h3><div style={{ color: dark ? "#9fb0c4" : "#64748b", fontSize: 13, marginTop: 4 }}>Her teknopark bağımsız süreç projesi olarak çalışır.</div></div>
        <button onClick={onClose} style={roundButton}><X size={17}/></button>
      </div>
      <select value={boardSelection} onChange={changeBoardSelection} style={inputStyle}>
        <option value="">Teknopark seçin</option>
        {TECHNOPARKS.map((name) => <option key={name} value={name}>{name}</option>)}
        <option value={OTHER_TECHNOPARK}>Diğer (listede yok)</option>
      </select>
      {boardSelection === OTHER_TECHNOPARK && <input autoFocus value={customBoardName} onChange={(event) => {
        const nextName = event.target.value;
        setCustomBoardName(nextName);
        if (!boardShortName || boardShortName === suggestedShortName) {
          setBoardShortName(getTechnoparkShortName(nextName));
        }
      }} placeholder="Teknoparkın resmi adını yazın" style={inputStyle}/>} 
      {boardSelection && <div>
        <label style={{ display: "block", marginBottom: 6, color: dark ? "#cbd5e1" : "#475569", fontSize: 12, fontWeight: 900 }}>
          Kanbanda gösterilecek kısa ad
        </label>
        <input value={boardShortName} onChange={(event) => setBoardShortName(event.target.value)} placeholder={suggestedShortName || "Örn. Dijitalpark"} style={inputStyle}/>
      </div>}
      <button disabled={saving} onClick={createBoard} style={primaryButton}>{saving ? "Oluşturuluyor..." : "Kanbanı Oluştur"}</button>
    </div>
  </div>;
}

const ErrorMessage = ({ message }) => <div style={{ marginBottom: 12, padding: 11, borderRadius: 11, background: "#fee2e2", color: "#b91c1c", fontWeight: 800 }}>{message}</div>;
const primaryButton = { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, border: 0, borderRadius: 10, padding: "10px 14px", background: "#0f766e", color: "#fff", fontWeight: 900, cursor: "pointer" };
const roundButton = { width: 32, height: 32, display: "inline-grid", placeItems: "center", border: "1px solid #cbd5e1", borderRadius: 9, background: "#fff", color: "#124254", cursor: "pointer" };
const inputStyle = { width: "100%", boxSizing: "border-box", padding: "11px 12px", border: "1px solid #cbd5e1", borderRadius: 10, background: "#fff", color: "#1f2937", fontSize: 14 };

export default TenantMonthlyProcessesView;
